import { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';
import { statusToBadgeVariant, type BadgeVariant } from '@/lib/statusBadge';

const badgeStyles = cva(
    'inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-bold border',
    {
        variants: {
            variant: {
                success:
                    'bg-success-soft text-success-text border-[rgb(155_190_169_/_0.4)]',
                info: 'bg-info-soft text-info-text border-[rgb(41_128_185_/_0.3)]',
                warning:
                    'bg-warning-soft text-warning-text border-[rgb(230_126_34_/_0.25)]',
                error: 'bg-error-soft text-error-text border-[rgb(192_57_43_/_0.2)]',
                neutral:
                    'bg-neutral-soft text-neutral-text border-[rgb(166_166_166_/_0.3)]',
                finalized:
                    'bg-finalized-soft text-finalized border-[rgb(108_52_131_/_0.3)]',
            },
        },
        defaultVariants: { variant: 'neutral' },
    },
);

export interface BadgeProps
    extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeStyles> { }

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
    ({ className, variant, ...props }, ref) => (
        <span ref={ref} className={cn(badgeStyles({ variant }), className)} {...props} />
    ),
);
Badge.displayName = 'Badge';

/** StatusBadge — renders the correct variant from a legacy status string. */
export function StatusBadge({
    status,
    icon,
    trailingIcon,
    className,
}: {
    status: string;
    icon?: React.ReactNode;
    trailingIcon?: React.ReactNode;
    className?: string;
}) {
    const variant: BadgeVariant = statusToBadgeVariant(status);
    return (
        <Badge variant={variant} className={className}>
            {icon}
            {status}
            {trailingIcon}
        </Badge>
    );
}
